import { ChevronDownIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  Tooltip,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { TooltipContentPlus } from "@f2e/ui/components/base-plus"
import { cn } from "@f2e/ui/lib/utils"

import { shortRelativeTime } from "@/lib/utils"
import { CopilotHistory } from "@/types"

interface CopilotHistoryDropdownProps {
  className?: string
  histories: CopilotHistory[]
  selectedId?: string
  onSelectHistory?: (history: CopilotHistory) => void
  disabled?: boolean
}

const CopilotHistoryDropdown = ({
  className,
  histories,
  selectedId,
  onSelectHistory,
  disabled = false,
}: CopilotHistoryDropdownProps) => {
  const { t } = useTranslation()

  const handleSelectHistory = (history: CopilotHistory) => {
    onSelectHistory?.(history)
  }

  const groupedHistory = groupHistoryByDate(histories)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          disabled={disabled}
          variant="ghost"
          size="sm"
          className={cn("max-w-64 py-0 gap-2 text-sm font-normal", className)}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="truncate">
                {histories.find((h) => h.id === selectedId)?.name ||
                  t("common.copilot.historyDropdown.newChat")}
              </span>
            </TooltipTrigger>
            <TooltipContentPlus
              side="bottom"
              className="max-w-[600px] truncate"
            >
              {histories.find((h) => h.id === selectedId)?.name ||
                t("common.copilot.historyDropdown.newChat")}
            </TooltipContentPlus>
          </Tooltip>
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="z-[70] w-80"
        data-copilot-portal
        data-testid="copilot-history-menu"
      >
        {Object.keys(groupedHistory).length === 0 ? (
          <DropdownMenuItem disabled>
            <span className="truncate text-muted-foreground">
              {t("common.copilot.historyDropdown.noHistory")}
            </span>
          </DropdownMenuItem>
        ) : (
          <>
            {Object.entries(groupedHistory).map(([group, histories], index) => (
              <div key={group}>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t(`common.copilot.historyDropdown.groups.${group}`)}
                </DropdownMenuLabel>
                {histories.map((history) => (
                  <DropdownMenuItem
                    key={history.id}
                    className="cursor-pointer flex-col items-start gap-1 py-2"
                    onSelect={() => handleSelectHistory(history)}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate font-medium">
                        {history.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {shortRelativeTime(new Date(history.createdAt))}
                      </span>
                    </div>
                  </DropdownMenuItem>
                ))}
                {index < Object.keys(groupedHistory).length - 1 && (
                  <DropdownMenuSeparator className="my-2 mx-2" />
                )}
              </div>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function groupHistoryByDate(
  histories: CopilotHistory[]
): Record<string, CopilotHistory[]> {
  const now = Date.now()
  const oneDayAgo = now - 1000 * 60 * 60 * 24
  const twoDaysAgo = now - 1000 * 60 * 60 * 24 * 2
  const sevenDaysAgo = now - 1000 * 60 * 60 * 24 * 7

  const groups: Record<string, CopilotHistory[]> = {
    Today: [],
    Yesterday: [],
    "Last 7 days": [],
    Older: [],
  }

  histories.forEach((history) => {
    const timestamp = new Date(history.createdAt).getTime()
    if (timestamp >= oneDayAgo) {
      groups.Today.push(history)
    } else if (timestamp >= twoDaysAgo) {
      groups.Yesterday.push(history)
    } else if (timestamp >= sevenDaysAgo) {
      groups["Last 7 days"].push(history)
    } else {
      groups.Older.push(history)
    }
  })

  // Filter out empty groups
  return Object.fromEntries(
    Object.entries(groups).filter(([, items]) => items.length > 0)
  )
}

export default CopilotHistoryDropdown
